import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Clock, Search, X, CheckCircle, Briefcase } from 'lucide-react';
import axios from 'axios';

// ── Helpers ──────────────────────────────────────────────────────────────────

function initials(first, last) {
    return ((first?.[0] ?? '') + (last?.[0] ?? '')).toUpperCase() || '?';
}

function formatDate(dt) {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatTime(dt) {
    if (!dt) return '';
    return new Date(dt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function countdown(dt) {
    if (!dt) return null;
    const diff = new Date(dt) - Date.now();
    if (diff <= 0) return null;
    const days = Math.floor(diff / 86400000);
    const hrs  = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}`;
    if (hrs  > 0) return `${hrs}h`;
    return 'soon';
}

function relativeDate(dt) {
    if (!dt) return '';
    const days = Math.floor((Date.now() - new Date(dt)) / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7)  return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} wk ago`;
    return `${Math.floor(days / 30)} mo ago`;
}

function parseTags(str) {
    return str ? str.split(',').map(t => t.trim()).filter(Boolean) : [];
}

function getToken() {
    return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
}

// ── Funnel components ─────────────────────────────────────────────────────────

function FunnelStep({ n, label, accent }) {
    return (
        <div className={`ap-funnel-step${accent ? ' is-accent' : ''}`}>
            <div className="ap-funnel-n">{n}</div>
            <div className="ap-funnel-l">{label}</div>
        </div>
    );
}

function FunnelArrow() {
    return (
        <svg className="ap-funnel-arrow" width="32" height="12" viewBox="0 0 32 12" fill="none">
            <path d="M2 6h26m0 0-4-4m4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );
}

// ── Next-up card ──────────────────────────────────────────────────────────────

function NextUpCard({ app }) {
    const job = app.job;
    const left = countdown(job.dateTime);
    const navigate = useNavigate();

    // Worker → poster: omit workerId so the backend treats the caller as the
    // worker side of this job's conversation.
    const startChat = async () => {
        try {
            const r = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/chat/conversations/start`,
                { jobId: job.id },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            navigate(`/messages?c=${r.data.id}`);
        } catch {
            alert('Could not open chat.');
        }
    };

    return (
        <article className="ap-next">
            <div className="ap-next-l">
                <span className="ap-next-eyebrow"><CheckCircle size={12} /> Next up · confirmed</span>
                <h3 className="ap-next-title">{job.title}</h3>
                <div className="ap-next-meta">
                    <span><Calendar size={13} /> {formatDate(job.dateTime)} · {formatTime(job.dateTime)}</span>
                    <span><MapPin size={13} /> {job.address || '—'}</span>
                </div>
            </div>
            <div className="ap-next-r">
                {left && (
                    <div className="ap-next-countdown">
                        <div className="ap-next-countdown-n">{left}</div>
                        <div className="ap-next-countdown-l">until start</div>
                    </div>
                )}
                <div className="ap-next-actions">
                    <button className="ix-btn ix-btn-primary ix-btn-sm"
                        onClick={startChat}>
                        Message {job.createdBy?.firstname ?? 'poster'}
                    </button>
                    <a
                        href={`https://maps.google.com/?q=${encodeURIComponent(job.address || '')}`}
                        target="_blank" rel="noreferrer"
                        className="ix-btn ix-btn-outline ix-btn-sm">
                        Get directions
                    </a>
                </div>
            </div>
        </article>
    );
}

// ── Pipeline column ───────────────────────────────────────────────────────────

function PipelineColumn({ colId, label, hint, apps, onWithdraw }) {
    return (
        <div className={`ap-col ap-col-${colId}`}>
            <header className="ap-col-head">
                <div className="ap-col-head-l">
                    <span className="ap-col-dot" />
                    <h3 className="ap-col-title">{label}</h3>
                </div>
                <span className="ap-col-count">{apps.length}</span>
            </header>
            <div className="ap-col-hint">{hint}</div>
            <div className="ap-col-body">
                {apps.length === 0
                    ? <div className="ap-col-empty">Nothing here.</div>
                    : apps.map(a => <MiniCard key={a.id} app={a} onWithdraw={onWithdraw} />)
                }
            </div>
        </div>
    );
}

// ── Mini card ─────────────────────────────────────────────────────────────────

function MiniCard({ app, onWithdraw }) {
    const job    = app.job;
    const poster = job.createdBy ?? {};
    const tags   = parseTags(job.tags);
    const isPending  = app.status === 'PENDING';
    const isAccepted = app.status === 'ACCEPTED';
    const left = isAccepted ? countdown(job.dateTime) : null;
    const navigate = useNavigate();

    // Worker → poster: omit workerId so the backend treats the caller as the
    // worker side of this job's conversation.
    const startChat = async () => {
        try {
            const r = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/chat/conversations/start`,
                { jobId: job.id },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            navigate(`/messages?c=${r.data.id}`);
        } catch {
            alert('Could not open chat.');
        }
    };

    return (
        <article className={`ap-mini ap-mini-${isPending ? 'pending' : 'accepted'}`}>
            <div className="ap-mini-head">
                <div className="ap-mini-pay">
                    <div className="ap-mini-pay-amt">£{job.payment?.toFixed(0) ?? '—'}</div>
                    {job.duration && <div className="ap-mini-pay-meta">{job.duration}</div>}
                </div>
            </div>

            <h4 className="ap-mini-title">{job.title}</h4>

            {tags.length > 0 && (
                <div className="ap-mini-tags">
                    {tags.slice(0, 2).map(t => <span key={t} className="ml-tag">#{t}</span>)}
                </div>
            )}

            <div className="ap-mini-meta"><Calendar size={11} /> {formatDate(job.dateTime)}, {formatTime(job.dateTime)}</div>
            <div className="ap-mini-meta"><MapPin size={11} /> {job.address || '—'}</div>

            {isPending && (
                <div className="ap-mini-pulse">
                    <span className="lg-dot-pulse" />
                    <span>Applied {relativeDate(app.applicationDate)}</span>
                </div>
            )}
            {isAccepted && left && (
                <div className="ap-mini-banner ap-mini-banner-accepted">
                    <Clock size={11} /> Starts in {left}
                </div>
            )}

            <footer className="ap-mini-foot">
                <span className="ap-mini-poster">
                    <span className="ml-cand-avatar">{initials(poster.firstname, poster.lastname)}</span>
                    <span>
                        <span className="ap-mini-poster-name">
                            {poster.firstname} {poster.lastname}
                            {poster.passportUrl && (
                                <svg width="10" height="10" viewBox="0 0 12 12" fill="var(--teal-700)">
                                    <circle cx="6" cy="6" r="6"/><path d="M4 6l1.5 1.5L8 4" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" fill="none"/>
                                </svg>
                            )}
                        </span>
                    </span>
                </span>
                <div className="ap-mini-actions">
                    {isPending && (
                        <button className="ap-mini-btn ap-mini-btn-danger" onClick={() => onWithdraw(app.id)}>
                            Withdraw
                        </button>
                    )}
                    {isAccepted && (
                        <button className="ap-mini-btn" onClick={startChat}>
                            Message
                        </button>
                    )}
                </div>
            </footer>
        </article>
    );
}

// ── History row ───────────────────────────────────────────────────────────────

function HistoryRow({ app, kind }) {
    const job    = app.job;
    const poster = job.createdBy ?? {};
    const tags   = parseTags(job.tags);

    return (
        <article className={`ap-hist-row ap-hist-${kind}`}>
            <div className="ap-hist-cell ap-hist-when">
                <div className="ap-hist-when-l">
                    {kind === 'completed' && `Done ${relativeDate(job.dateTime)}`}
                    {kind === 'declined'  && `Declined ${relativeDate(app.applicationDate)}`}
                </div>
                <div className="ap-hist-when-sub">Applied {relativeDate(app.applicationDate)}</div>
            </div>
            <div className="ap-hist-cell ap-hist-job">
                <div className="ap-hist-job-title">{job.title}</div>
                <div className="ap-hist-job-meta">{tags.slice(0, 2).map(t => `#${t}`).join(' ')}</div>
            </div>
            <div className="ap-hist-cell ap-hist-poster">
                <span className="ml-cand-avatar">{initials(poster.firstname, poster.lastname)}</span>
                <span>
                    <span className="ap-mini-poster-name">{poster.firstname} {poster.lastname}</span>
                </span>
            </div>
            <div className="ap-hist-cell ap-hist-tail">
                {kind === 'completed' && (
                    <>
                        <div className="ap-hist-stars">★★★★★</div>
                        <div className="ap-hist-earn">+£{job.payment?.toFixed(0) ?? '—'}</div>
                    </>
                )}
                {kind === 'declined' && (
                    <span className="ap-hist-status ap-hist-status-rejected">Declined</span>
                )}
            </div>
        </article>
    );
}

function EmptyRow({ kind }) {
    const copy = {
        completed: 'Jobs you complete will appear here.',
        declined:  'Nothing declined — keep applying.',
    }[kind] ?? '';
    return <div className="ap-hist-empty">{copy}</div>;
}

// ── Main component ────────────────────────────────────────────────────────────

const MyApplications = ({ user }) => {
    const [apps, setApps]         = useState([]);
    const [loading, setLoading]   = useState(true);
    const [query, setQuery]       = useState('');
    const [historyTab, setHistoryTab] = useState('completed');
    const [toast, setToast]       = useState('');

    const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

    const load = useCallback(async () => {
        if (!user?.id) return;
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/users/${user.id}/applied`,
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            setApps(res.data ?? []);
        } catch (e) {
            console.error('Failed to load applications', e);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { load(); }, [load]);

    const handleWithdraw = async (appId) => {
        if (!window.confirm('Withdraw this application?')) return;
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_BASE_URL}/api/applications/${appId}`,
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            setApps(prev => prev.filter(a => a.id !== appId));
            flash('Application withdrawn.');
        } catch {
            flash('Could not withdraw — please try again.');
        }
    };

    // ── Filtering ──
    const q = query.trim().toLowerCase();
    const matches = (a) => !q || a.job?.title?.toLowerCase().includes(q)
        || `${a.job?.createdBy?.firstname} ${a.job?.createdBy?.lastname}`.toLowerCase().includes(q)
        || parseTags(a.job?.tags).some(t => t.includes(q));

    const filtered = apps.filter(matches);

    // Buckets
    const pendingApps   = filtered.filter(a => a.status === 'PENDING');
    const acceptedApps  = filtered.filter(a => a.status === 'ACCEPTED' && a.job?.jobStatus !== 'COMPLETED');
    const completedApps = filtered.filter(a => a.status === 'ACCEPTED' && a.job?.jobStatus === 'COMPLETED');
    const declinedApps  = filtered.filter(a => a.status === 'REJECTED');

    // Stats
    const totalApplied = apps.filter(a => a.status !== undefined).length;
    const wonCount     = apps.filter(a => a.status === 'ACCEPTED').length;
    const decidedCount = apps.filter(a => ['ACCEPTED','REJECTED'].includes(a.status)).length;
    const winRate      = decidedCount ? Math.round((wonCount / decidedCount) * 100) : 0;
    const now = new Date();
    const earnedThisMonth = apps
        .filter(a => a.status === 'ACCEPTED' && a.job?.jobStatus === 'COMPLETED')
        .filter(a => {
            const d = new Date(a.job?.dateTime);
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        })
        .reduce((s, a) => s + (a.job?.payment ?? 0), 0);

    // Next upcoming accepted shift
    const nextShift = acceptedApps
        .filter(a => new Date(a.job?.dateTime) > now)
        .sort((a, b) => new Date(a.job.dateTime) - new Date(b.job.dateTime))[0];

    if (loading) return <div className="ap-loading">Loading your applications…</div>;

    if (!user) return (
        <div className="ap-loading">
            <Link to="/login" className="ix-btn ix-btn-primary">Log in to see your applications</Link>
        </div>
    );

    return (
        <div className="ap-page">
            <main className="ap-main">
                <div className="ix-container">

                    {/* ── Hero ── */}
                    <header className="ap-hero">
                        <div className="ap-hero-l">
                            <span className="eyebrow">Your work</span>
                            <h1 className="ap-hero-title">
                                <span className="serif">Hello,</span> {user.firstname || user.username}.
                            </h1>
                            <p className="ap-hero-sub">
                                You've got {pendingApps.length} {pendingApps.length === 1 ? 'bid' : 'bids'} out
                                {acceptedApps.length > 0 && <> and {acceptedApps.length} {acceptedApps.length === 1 ? 'shift' : 'shifts'} locked in</>}.
                            </p>
                        </div>
                        <div className="ap-funnel">
                            <FunnelStep n={totalApplied} label="Applied" />
                            <FunnelArrow />
                            <FunnelStep n={wonCount} label="Accepted" accent />
                            <FunnelArrow />
                            <FunnelStep n={completedApps.length} label="Done" />
                        </div>
                    </header>

                    {/* ── Next-up card ── */}
                    {nextShift && <NextUpCard app={nextShift} />}

                    {/* ── Toolbar ── */}
                    <div className="ap-toolbar">
                        <div className="ap-search">
                            <Search size={14} />
                            <input
                                placeholder="Search by job, poster, or tag…"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                            />
                            {query && (
                                <button className="ap-search-clear" onClick={() => setQuery('')} aria-label="Clear">
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                        <div className="ap-toolbar-stats">
                            <span><strong>{winRate}%</strong> <span className="mono">win rate</span></span>
                            <span className="ap-divider" />
                            <span><strong>£{earnedThisMonth.toFixed(0)}</strong> <span className="mono">earned this month</span></span>
                        </div>
                        <Link to="/get-job" className="ix-btn ix-btn-primary ix-btn-sm">
                            <Briefcase size={14} /> Find more jobs
                        </Link>
                    </div>

                    {/* ── Pipeline ── */}
                    <div className="ap-pipeline">
                        <PipelineColumn
                            colId="applied"
                            label="Applied"
                            hint="Waiting for a decision"
                            apps={pendingApps}
                            onWithdraw={handleWithdraw}
                        />
                        <PipelineColumn
                            colId="accepted"
                            label="Accepted"
                            hint="You got it"
                            apps={acceptedApps}
                            onWithdraw={handleWithdraw}
                        />
                    </div>

                    {/* ── History ── */}
                    <section className="ap-history">
                        <header className="ap-history-head">
                            <h2 className="ap-history-title">
                                History{' '}
                                <span className="ap-history-count">{completedApps.length + declinedApps.length}</span>
                            </h2>
                            <div className="ap-segment">
                                <button
                                    className={`ap-segment-btn${historyTab === 'completed' ? ' is-on' : ''}`}
                                    onClick={() => setHistoryTab('completed')}>
                                    Completed <span className="ap-tab-count">{completedApps.length}</span>
                                </button>
                                <button
                                    className={`ap-segment-btn${historyTab === 'declined' ? ' is-on' : ''}`}
                                    onClick={() => setHistoryTab('declined')}>
                                    Declined <span className="ap-tab-count">{declinedApps.length}</span>
                                </button>
                            </div>
                        </header>

                        <div className="ap-history-feed">
                            {historyTab === 'completed' && (
                                completedApps.length > 0
                                    ? completedApps.map(a => <HistoryRow key={a.id} app={a} kind="completed" />)
                                    : <EmptyRow kind="completed" />
                            )}
                            {historyTab === 'declined' && (
                                declinedApps.length > 0
                                    ? declinedApps.map(a => <HistoryRow key={a.id} app={a} kind="declined" />)
                                    : <EmptyRow kind="declined" />
                            )}
                        </div>
                    </section>

                </div>
            </main>

            {toast && <div className="ml-toast">{toast}</div>}
        </div>
    );
};

export default MyApplications;
